<template>
  <div class="score-action">
    <p>Point outcome:</p>
    <div class="who-won">
      <label class="outcome outcome-won" :class="{ selected: selectedOutcome === 'Won', 'input-disabled': isDisabled }">
        <input type="radio" value="Won" v-model="selectedOutcome" @click="toggleOutcome('Won')" :disabled="disabled" />
        Won
      </label>
      <label class="outcome outcome-lost" :class="{ selected: selectedOutcome === 'Lost', 'input-disabled': isDisabled }">
        <input type="radio" value="Lost" v-model="selectedOutcome" @click="toggleOutcome('Lost')" :disabled="disabled" />
        Lost
      </label>
    </div>
    <div v-if="selectedOutcome">
      <label>How the point was {{ selectedOutcome.toLowerCase() }}:</label>
      <div class="point-detail-radios">
        <div v-for="option in pointDetailOptions" :key="option">
          <label
            :for="'point-detail-' + option"
            class="point-detail-radio"
            :class="{ selected: pointDetail === option, 'input-disabled': isOptionDisabled(option) || isPointDetailDisabled}"
          >
            <input
              type="radio"
              :id="'point-detail-' + option"
              :value="option"
              v-model="pointDetail"
              @change="updatePointDetail"
              :disabled="isOptionDisabled(option) || isPointDetailDisabled"
            />
            {{ option }}
          </label>
        </div>
      </div>
    </div>
    <div v-show="pointDetail && showPlayerSection">
      <label for="player" v-if="selectedOutcome">Player who {{ selectedOutcome.toLowerCase() }} the point:</label>
      <div class="player-radios">
        <div v-for="item in sortedStartingPlayers" :key="item.rotationPosition">
          <label
            :for="'scoring-player-' + item.rotationPosition"
            class="player-radio" 
            :class="{ selected: isSelectedPlayer(item.player), 'input-disabled': isPlayerDisabled }"
          >
            <input
              type="checkbox"
              :id="'scoring-player-' + item.rotationPosition"
              :value="item.player"
              :checked="isSelectedPlayer(item.player)"
              @change="togglePlayerSelection(item.player)"
              :disabled="isPlayerDisabled || !item.player"
            />
            <span v-if="item.player">{{ item.player.number }}</span>
            {{ item.player ? item.player.name : '' }}
            <span v-if="item.roleCode" class="player-role">{{ item.roleCode }}</span>
            <img
              v-if="item.player && item.player.photo"
              class="player-photo"
              :src="getPhotoUrl(item.player)"
              :alt="item.player ? item.player.name : ''"
            />
          </label>
        </div>
        <div>
          <!-- Add the "Team" option -->
          <label
            for="scoring-player-team"
            class="player-radio"
            :class="{ selected: isSelectedPlayer('Team'), 'input-disabled': isPlayerDisabled }"
          >
            <input
              type="checkbox"
              id="scoring-player-team"
              value="Team"
              :checked="isSelectedPlayer('Team')"
              @change="togglePlayerSelection('Team')"
              :disabled="isPlayerDisabled"
            />
            Team
          </label>
        </div>
        <div v-if="libero">
          <!-- Add the "Libero" option -->
          <label
            for="scoring-player-libero"
            class="player-radio"
            :class="{ selected: isSelectedPlayer(libero), 'input-disabled': isPlayerDisabled }"
          >
            <input
              type="checkbox"
              id="scoring-player-libero"
              :value="libero"
              :checked="isSelectedPlayer(libero)"
              @change="togglePlayerSelection(libero)"
              :disabled="isPlayerDisabled"
            />
            <span>{{ libero.number }}</span>
            {{ libero.name }}
            <span class="player-role">L</span>
            <img
              v-if="libero && libero.photo"
              class="player-photo"
              :src="getPhotoUrl(libero)"
              :alt="libero.name"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';

export default {
  props: {
    initialOutcome: {
      type: String,
      default: null
    },
    initialPointDetail: {
      type: String,
      default: ''
    },
    initialPlayer: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isPlayerDisabled: {
      type: Boolean,
      default: false
    },
    currentStartPoint: {
      type: String,
      default: ''
    },
    serveRating: {
      type: Number,
      default: null
    },
    passRating: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      selectedOutcome: this.initialOutcome,
      pointDetail: this.initialPointDetail,
      selectedPlayer: this.initialPlayer && this.initialPlayer.id ? this.initialPlayer : null,
      secondSelectedPlayer: null,
      currentLineup: store.currentLineup,
      processing: false
    };
  },
  computed: {
    pointDetailOptions() {
      if (this.selectedOutcome === 'Won') {
        return [
          'Kill',
          'Kill block (solo)',
          'Kill block (assist)',
          'Ace',
          'Opp. attack error',
          'Opp. block error',
          'Opp. serve error',
          'Opp. other error'
        ];
      } else if (this.selectedOutcome === 'Lost') {
        return [
          'Attack error',
          'Block error',
          'Serve error',
          'Other error',
          'Opp. kill',
          'Opp. kill block',
          'Opp. ace'
        ];
      }
      return [];
    },
    showPlayerSection() {
      const relevantPointDetails = [
        'Kill',
        'Kill block (solo)',
        'Kill block (assist)',
        'Ace',
        'Attack error',
        'Block error',
        'Serve error',
        'Other error',
        'Opp. kill block',
        'Opp. ace'
      ];
      return relevantPointDetails.includes(this.pointDetail);
    },
    isPointDetailDisabled() {
      return this.serveRating === 0 || this.serveRating === 4 || this.passRating === 0;
    },
    isDisabled() {
      return this.disabled;
    },
    libero() {
      return store.currentLibero;
    },
    sortedStartingPlayers() {
      return this.currentLineup.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);
    }
  },
  methods: {
    getPhotoUrl(player) {
      try {
        if (player && player.photo) {
          return require(`@/assets/${player.photo}`);
        }
        return require('@/assets/player-photos/placeholder.png');
      } catch (error) {
        return require('@/assets/player-photos/placeholder.png');
      }
    },
    toggleOutcome(value) {
      if (this.isDisabled || this.processing) {
        return;
      }

      this.processing = true;

      if (this.selectedOutcome === value) {
        // Deselecting - clear everything
        this.selectedOutcome = null;
        this.pointDetail = '';
        this.selectedPlayer = null;
        this.secondSelectedPlayer = null;

        this.$nextTick(() => {
          this.emitAllState();
          this.$nextTick(() => {
            this.processing = false;
          });
        });
        return;
      }

      const wasDifferent = this.selectedOutcome && this.selectedOutcome !== value;
      this.selectedOutcome = value;

      if (wasDifferent) {
        // Switching outcome - clear detail and players
        this.pointDetail = '';
        this.selectedPlayer = null;
        this.secondSelectedPlayer = null;
      }

      this.$nextTick(() => {
        this.emitAllState();
        this.$nextTick(() => {
          this.processing = false;
        });
      });
    },
    emitAllState() {
      // Emit all state in a single call to avoid rapid sequential emits
      this.$emit('update-first-set', 'scoreAction', {
        outcome: this.selectedOutcome,
        pointDetail: this.pointDetail,
        player: this.selectedPlayer,
        secondPlayer: this.secondSelectedPlayer
      });
    },
    updateSelection() {
      if (!this.processing) {
        this.emitAllState();
      }
    },
    updatePointDetail() {
      if (!this.processing) {
        this.emitAllState();
      }
    },
    updatePlayer() {
      if (!this.processing) {
        this.emitAllState();
      }
    },
    togglePlayerSelection(player) {
      if (this.processing) {
        return;
      }

      console.log('togglePlayerSelection click', {
        player,
        pointDetail: this.pointDetail,
        selectedPlayer: this.selectedPlayer,
        secondSelectedPlayer: this.secondSelectedPlayer
      });

      if (this.pointDetail === 'Kill block (assist)') {
        if (this.selectedPlayer === player) {
          this.selectedPlayer = null;
        } else if (this.secondSelectedPlayer === player) {
          this.secondSelectedPlayer = null;
        } else if (!this.selectedPlayer) {
          this.selectedPlayer = player;
        } else if (!this.secondSelectedPlayer) {
          this.secondSelectedPlayer = player;
        } else {
          this.selectedPlayer = player;
          this.secondSelectedPlayer = null;
        }
      } else {
        this.selectedPlayer = player;
        this.secondSelectedPlayer = null;
      }

      console.log('togglePlayerSelection after', {
        selectedPlayer: this.selectedPlayer,
        secondSelectedPlayer: this.secondSelectedPlayer
      });

      this.emitAllState();
    },
    isSelectedPlayer(player) {
      return this.selectedPlayer === player || this.secondSelectedPlayer === player;
    },
    isOptionDisabled(option) {
      if (this.currentStartPoint === 'Receive' && option === 'Ace') {
        return true;
      }
      if (this.currentStartPoint === 'Receive' && option === 'Serve error') {
        return true;
      }
      if (this.currentStartPoint === 'Receive' && this.passRating !== 0 && option === 'Opp. ace') {
        return true;
      }
      
      if (this.currentStartPoint === 'Serve' && option === 'Opp. ace') {
        return true;
      }
      if (this.currentStartPoint === 'Serve' && option === 'Opp. serve error') {
        return true;
      }
      if (this.currentStartPoint === 'Serve') {
        if (option === 'Serve error' && this.serveRating !== 0) {
          return true;
        }
        if (option === 'Ace' && this.serveRating !== 4) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    initialOutcome(newOutcome) {
      this.selectedOutcome = newOutcome;
    },
    initialPointDetail(newPointDetail) {
      this.pointDetail = newPointDetail;
    },
    initialPlayer(newPlayer) {
      console.log('initialPlayer watcher', newPlayer);
      // Check if player is valid (has id or is a valid object with data)
      const hasValidPlayer = newPlayer &&
                             typeof newPlayer === 'object' &&
                             Object.keys(newPlayer).length > 0 &&
                             (newPlayer.id || newPlayer.name);

      this.selectedPlayerNumber = hasValidPlayer && newPlayer.number ? newPlayer.number : '';
      this.selectedPlayer = hasValidPlayer ? newPlayer : null;
    }
  }
};
</script>

<style scoped lang="scss">
.score-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.who-won {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
}

.outcome {
  margin: 0 5px 20px 5px;
  padding: 20px;
  background: var(--grey-15);
  color: var(--grey-900);
  display: inline-flex;
  border-radius: 5px;
  width: 100px;
  justify-content: center;
  align-content: center;
  gap: 10px;
  font-size: 16px;
  cursor: pointer;
  color: var(--grey-900);

  &.selected {
    background: var(--grey-900);
    color: var(--white);
    font-weight: bold;

    &.outcome-won {
      background: var(--green-100);

      &.selected {
        background: var(--green-100);
        color: var(--grey-900);
      }
    }
    &.outcome-lost {
      background: var(--red-50);

      &.selected {
        background: var(--grey-900);
        color: var(--white);
      }
    }
  }

  input {
    display: none;
  }
  
  &.input-disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.point-detail-radios {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 10px 0 20px 0;
  color: var(--grey-900);
  text-align: center;

  .point-detail-radio {
    margin: 0;
    padding: 8px 16px;
    background: var(--grey-15);
    display: inline-flex;
    border-radius: 5px;
    width: auto;
    justify-content: center;
    align-content: center;
    gap: 10px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1.25;

    input {
      display: none;
    }

    &.input-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &.selected {
      background: var(--grey-75);
      font-weight: bold;
    }
  }
}

.player-radios {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;

  .player-radio {
    margin: 10px 0 20px 0;
    padding: 15px 5px;
    background: var(--grey-15);
    border-radius: 100px;
    width: 120px;
    justify-content: center;
    align-content: center;
    gap: 4px;
    cursor: pointer;
    aspect-ratio: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 11px;
    line-height: 1;
    color: var(--grey-900);
    text-align: center;
    overflow: hidden;
    box-sizing: border-box;

    span {
      font-weight: 900;
    }

    .player-role {
      font-size: 10px;
      font-weight: 700;
      color: var(--grey-800);
    }

    input {
      display: none;
    }

    &.input-disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &.selected {
      background: var(--grey-900);
      color: var(--white);
      font-weight: bold;

      .player-role {
        color: var(--white);
      }
    }

    &.error {
      background: var(--red-50);

      &.selected {
        background: var(--grey-900);
        color: var(--white);
      }
    }
  }
}

.player-photo {
  width: 80px;
  height: 80px;
  object-fit: cover;
  vertical-align: middle;
  margin: -5px 0 -25px;
}
</style>
